import React, { useReducer } from 'react';
import PropTypes from 'prop-types';
import getIcon from 'utils/icon-mapper';
import I18n from 'utils/I18n';
import styles from './styles.scss';
import useFeedAnalytics from '../../hooks/useFeedAnalytics';
import {
  pageProps,
  viewingAthleteProps,
  cursorDataProps,
  feedTypeProps,
  indexProps
} from '../../props/feed-prop-types';
import FeedEntityContext from '../../state/FeedEntityContext';
import { init, reducer } from '../../state/feed-entity-reducer';
import { buildInitialState } from '../../state/feed-entity-init-state';

import AthleteJoinEntry from '../shared/AthleteJoinEntry';

const i18nPrefix = 'components.feed.challenges';

const ChallengeJoin = ({
  viewingAthlete,
  clubId,
  page,
  entity,
  feedType,
  index,
  cursorData,
  rowData,
  athleteProfileId
}) => {
  const { trackAnalytics, intersectionObserverRef } = useFeedAnalytics({
    page,
    entity,
    entityId: rowData.entity_id,
    athleteProfileId,
    feedType,
    cursorData,
    viewingAthleteId: viewingAthlete.id,
    clubId
  });

  const [entityState] = useReducer(
    reducer,
    buildInitialState({
      trackAnalytics
    }),
    init
  );

  const providerState = {
    entityState
  };

  const logo = (
    <a
      href={rowData.challenge_url}
      alt={rowData.challenge_name}
      onClick={() => {
        entityState.trackAnalytics({
          action: 'click',
          element: 'challenge_image'
        });
      }}
    >
      <img
        className={styles.logo}
        src={rowData.entry_logo_url}
        alt={rowData.challenge_name}
      />
    </a>
  );

  const bodyProps = {
    entity,
    title: rowData.title,
    badge: logo,
    entityName: rowData.challenge_name,
    entityUrl: rowData.challenge_url,
    entityIcon: (
      <div data-testid="sports-icon">
        {getIcon(`sports_${rowData.displayed_icon}_normal_xsmall`)}
      </div>
    ),
    entityMemberCount: rowData.participant_count,
    isMember: !rowData.challenge_joinable,
    secondaryText: rowData.challenge_ended ? '' : rowData.days_left,
    intersectionObserverRef,
    index,
    cursorData,
    entityId: rowData.entity_id,
    minimumAge:
      rowData.gating_conditions && rowData.gating_conditions.minimum_age,
    joinMethod: rowData.join_method,
    viewingAthlete,
    labels: {
      join: rowData.virtual_race
        ? I18n.t(`${i18nPrefix}.view_virtual_race`)
        : I18n.t(`${i18nPrefix}.view_challenge`),
      view: rowData.join_button_text,
      joined: I18n.t(`${i18nPrefix}.joined`),
      memberCountKey: `${i18nPrefix}.participants`,
      error: I18n.t(`${i18nPrefix}.error`)
    }
  };

  return (
    <FeedEntityContext.Provider value={providerState}>
      <AthleteJoinEntry {...bodyProps} />
    </FeedEntityContext.Provider>
  );
};

ChallengeJoin.propTypes = {
  // feed entity could be Activity, group_activity, Post etc
  entity: PropTypes.oneOf(['Challenge']).isRequired,
  viewingAthlete: viewingAthleteProps.isRequired,
  clubId: PropTypes.string.isRequired,
  athleteProfileId: PropTypes.string.isRequired,
  page: pageProps.isRequired,
  cursorData: cursorDataProps.isRequired,
  feedType: feedTypeProps.isRequired,
  // range of number 0...19 representing rows returned by the server per feed load
  index: indexProps.isRequired,
  rowData: PropTypes.shape({
    title: PropTypes.string.isRequired,
    entry_logo_url: PropTypes.string.isRequired,
    challenge_name: PropTypes.string.isRequired,
    challenge_url: PropTypes.string.isRequired,
    entity_id: PropTypes.number.isRequired,
    displayed_icon: PropTypes.string.isRequired,
    participant_count: PropTypes.number.isRequired,
    challenge_joinable: PropTypes.bool.isRequired,
    join_method: PropTypes.string.isRequired,
    join_button_text: PropTypes.string.isRequired,
    challenge_ended: PropTypes.bool.isRequired,
    days_left: PropTypes.string,
    virtual_race: PropTypes.bool,
    gating_conditions: PropTypes.shape({
      minimum_age: PropTypes.number
    })
  }).isRequired
};

export default ChallengeJoin;
